import React from 'react';
import { CallGetTempImageUrl } from '@library/request/callProduct';
import { ExtensionType, ImageFileSize, PropTypes } from '@components/molecules/input/inputType';

const ImageInput = (props: PropTypes) => {
  const { readOnly, extension, size, maxSize, onChangeHandler, url, onClickDelete } = props;

  const convertToMB = (byte: number) => byte / 100000;

  const isValidExtension = async (files: File[]): Promise<void> => {
    const isValidPromises = files.map((file) => {
      const { type } = file;

      if (!extension) {
        return Promise.resolve();
      }

      if (!type.match('image.*')) {
        return Promise.reject(new Error('이미지 파일만 가능합니다.'));
      }

      if (extension === ExtensionType.jpeg) {
        // 이미지 파일 전체
        if (!type.match(ExtensionType.imageJpeg)) {
          return Promise.reject(new Error('이미지 파일은 (.jpg)만 업로드 가능합니다.'));
        }
      }

      if (extension === ExtensionType.jpegPng) {
        if (!(type.match(ExtensionType.imageJpeg) || type.match(ExtensionType.imagePng))) {
          return Promise.reject(new Error('이미지 파일은 (jpg/png)만 업로드 가능합니다.'));
        }
      }
      return Promise.resolve();
    });

    try {
      // 모든 파일에 대한 확장자 검사를 병렬로 진행
      await Promise.all(isValidPromises);
    } catch (err) {
      // 여러 개의 파일 중에서 하나라도 유효하지 않은 경우에 대한 에러 처리 로직 추가
      if (err.name === 'TypeError') {
        const msg = '다시 시도해주세요.';
        alert(msg);
      } else {
        alert(err.message);
      }
      console.log(`err: ${err.message}`);
      throw err;
    }
  };

  const isValidSize = async (file: File): Promise<void> => {
    if (!size && !maxSize) {
      return;
    }

    // 이미지 크기 체크
    if (maxSize) {
      // file.size is in bytes
      if (file.size > maxSize) {
        throw new Error(`${Math.floor(convertToMB(maxSize))}MB 이하의 이미지만 업로드 가능합니다.`);
      }
    }
  };

  const isValidImage = async (files: File[]): Promise<File[]> => {
    if (!files || files.length === 0) {
      throw new Error('파일이 선택되지 않았습니다.');
    }

    try {
      // 확장자 체크
      await isValidExtension(files);

      // 크기 체크
      const isValidSizePromises = files.map(isValidSize);
      await Promise.all(isValidSizePromises);
    } catch (err) {
      // 에러 처리 로직 추가
      if (err.name === 'TypeError') {
        const msg = '다시 시도해주세요.';
        alert(msg);
      } else {
        alert(err.message);
      }
      console.log(`err: ${err.message}`);
      throw err;
    }

    // 모든 파일이 유효하면 파일 배열을 반환
    return files;
  };

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;

    if (!files || files.length === 0) {
      return;
    }

    const filesArray = Array.from(files);

    const promises = filesArray.map(async (file) => {
      const s: ImageFileSize = { width: 0, height: 0 };

      try {
        const isValid = await isValidImage([file]);

        if (isValid) {
          const reader = new FileReader();

          // get size
          reader.onload = async (e: any) => {
            const tempImage = new Image();
            tempImage.src = e.target.result;
            tempImage.onload = async () => {
              s.width = tempImage.width;
              s.height = tempImage.height;
            };
          };

          await reader.readAsDataURL(file);

          // get temp url
          const formData = new FormData();
          formData.append('tempFile', file);
          const { data } = await CallGetTempImageUrl(formData);
          // send temp url to onChangeHandler
          onChangeHandler && onChangeHandler(data.returnUrl, s);
        }
      } catch (err) {
        if (err.name === 'TypeError') {
          const msg = '다시 시도해주세요.';
          alert(msg);
        } else {
          alert(err.message);
        }
        console.log(`err: ${err.message}`);
      }
    });

    await Promise.all(promises);

    // reset image files
    e.target.value = '';
  };

  return (
    <>
      <div className="wrap-attach">
        <div id="attachFile" className="attach-file">
          {url ? (
            <>
              <img className="thumb-img" src={url} alt="메뉴이미지" />
              <img
                onClick={onClickDelete}
                className="btn-del"
                src={'//image.thecupping.co.kr/partners/pc-btn-x-circle.svg'}
                alt="삭제 아이콘"
              />
            </>
          ) : (
            <label htmlFor="imageUrl">
              <span className="screen-out">이미지 추가</span>
              <input
                type="file"
                accept="image/*"
                id="imageUrl"
                className="inp-attach"
                onChange={onChange}
                readOnly={readOnly}
                multiple
              />
            </label>
          )}
        </div>
        <ul className="list-tips">
          <li>이미지는 1개만 첨부 가능합니다.</li>
          <li>PNG, JPG 파일만 첨부 가능하며, 용량은 5MB를 넘을 수 없습니다.</li>
        </ul>
      </div>
    </>
  );
};

export default ImageInput;
